import { Type, type Static } from "typebox";
import { ProductKeySchema } from "./identity.ts";

const NonNegativeSafeIntegerSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});
const NullableNonNegativeSafeIntegerSchema = Type.Union([
  NonNegativeSafeIntegerSchema,
  Type.Null(),
]);
const UuidSchema = Type.String({ format: "uuid" });
const DateTimeSchema = Type.String({ format: "date-time" });
const DateSchema = Type.String({ format: "date" });
const BoundedMetadataStringSchema = Type.String({ minLength: 1, maxLength: 100 });
const RunKindSchema = Type.String({ minLength: 1, maxLength: 64 });

export const AnalyticsProductKeySchema = Type.Union([
  Type.Literal("platform"),
  ProductKeySchema,
]);
export type AnalyticsProductKey = Static<typeof AnalyticsProductKeySchema>;

export const AnalyticsProductFilterV1Schema = Type.Union([
  Type.Literal("all"),
  AnalyticsProductKeySchema,
]);
export type AnalyticsProductFilterV1 = Static<typeof AnalyticsProductFilterV1Schema>;

export const AnalyticsDaysV1Schema = Type.Union([
  Type.Literal(7),
  Type.Literal(30),
  Type.Literal(90),
  Type.Literal(365),
]);
export type AnalyticsDaysV1 = Static<typeof AnalyticsDaysV1Schema>;

export const SafeRunErrorCodeSchema = Type.Union([
  Type.Literal("timeout"),
  Type.Literal("provider_unavailable"),
  Type.Literal("rate_limited"),
  Type.Literal("validation_failed"),
  Type.Literal("internal_error"),
]);
export type SafeRunErrorCode = Static<typeof SafeRunErrorCodeSchema>;

export const ApprovedFeatureKeySchema = Type.Union([
  Type.Literal("organization_switched"),
  Type.Literal("member_invited"),
  Type.Literal("settings_updated"),
  Type.Literal("project_created"),
  Type.Literal("plan_generated"),
  Type.Literal("sop_exported"),
  Type.Literal("intake_section_saved"),
  Type.Literal("document_uploaded"),
  Type.Literal("intake_completed"),
  Type.Literal("brief_viewed"),
  Type.Literal("proposal_decided"),
  Type.Literal("agent_run_requested"),
  Type.Literal("onboarding_completed"),
  Type.Literal("initiative_updated"),
  Type.Literal("decision_recorded"),
]);
export type ApprovedFeatureKey = Static<typeof ApprovedFeatureKeySchema>;

export const SuiteEventInputSchema = Type.Union([
  Type.Object({
    name: Type.Literal("app.opened"),
  }, { additionalProperties: false }),
  Type.Object({
    name: Type.Literal("product_session.started"),
  }, { additionalProperties: false }),
  Type.Object({
    name: Type.Literal("product_session.ended"),
    reason: Type.Union([
      Type.Literal("logout"),
      Type.Literal("expired"),
      Type.Literal("revoked"),
      Type.Literal("closed"),
    ]),
  }, { additionalProperties: false }),
  Type.Object({
    name: Type.Literal("run.started"),
    traceId: UuidSchema,
    runKind: RunKindSchema,
  }, { additionalProperties: false }),
  Type.Object({
    name: Type.Literal("run.completed"),
    traceId: UuidSchema,
    runKind: RunKindSchema,
    durationMs: NonNegativeSafeIntegerSchema,
  }, { additionalProperties: false }),
  Type.Object({
    name: Type.Literal("run.failed"),
    traceId: UuidSchema,
    runKind: RunKindSchema,
    durationMs: NonNegativeSafeIntegerSchema,
    errorCode: SafeRunErrorCodeSchema,
  }, { additionalProperties: false }),
  Type.Object({
    name: Type.Literal("run.cancelled"),
    traceId: UuidSchema,
    runKind: RunKindSchema,
    durationMs: NonNegativeSafeIntegerSchema,
  }, { additionalProperties: false }),
  Type.Object({
    name: Type.Literal("ai.usage_recorded"),
    traceId: UuidSchema,
    provider: BoundedMetadataStringSchema,
    model: BoundedMetadataStringSchema,
    inputTokens: Type.Optional(NonNegativeSafeIntegerSchema),
    outputTokens: Type.Optional(NonNegativeSafeIntegerSchema),
    cacheReadTokens: Type.Optional(NonNegativeSafeIntegerSchema),
    cacheCreation5mTokens: Type.Optional(NonNegativeSafeIntegerSchema),
    cacheCreation1hTokens: Type.Optional(NonNegativeSafeIntegerSchema),
  }, { additionalProperties: false }),
  Type.Object({
    name: Type.Literal("feature.used"),
    featureKey: ApprovedFeatureKeySchema,
  }, { additionalProperties: false }),
]);
export type SuiteEventInput = Static<typeof SuiteEventInputSchema>;

export const AnalyticsBatchEventV1Schema = Type.Object({
  eventId: UuidSchema,
  occurredAt: DateTimeSchema,
  productSessionId: UuidSchema,
  sessionEpoch: NonNegativeSafeIntegerSchema,
  event: SuiteEventInputSchema,
}, { additionalProperties: false });
export type AnalyticsBatchEventV1 = Static<typeof AnalyticsBatchEventV1Schema>;

export const AnalyticsBatchV1Schema = Type.Object({
  version: Type.Literal(1),
  events: Type.Array(AnalyticsBatchEventV1Schema, { minItems: 1, maxItems: 100 }),
}, { additionalProperties: false });
export type AnalyticsBatchV1 = Static<typeof AnalyticsBatchV1Schema>;

export const AnalyticsQueryV1Schema = Type.Object({
  days: Type.Optional(AnalyticsDaysV1Schema),
  product: Type.Optional(AnalyticsProductFilterV1Schema),
}, { additionalProperties: false });
export type AnalyticsQueryV1 = Static<typeof AnalyticsQueryV1Schema>;

/** F5 fleet rows are page-bounded independently from the exact fleet totals. */
export const ANALYTICS_FLEET_MAX_PAGE_SIZE = 100;
// A cursor embeds a bounded organization name as base64url JSON. The 2 KiB
// ceiling covers a 200-code-unit Unicode name even when JSON escaping expands
// every character, while remaining a deliberately small browser query value.
export const ANALYTICS_FLEET_MAX_CURSOR_LENGTH = 2_048;
const AnalyticsOpaqueCursorSchema = Type.String({
  minLength: 1,
  maxLength: ANALYTICS_FLEET_MAX_CURSOR_LENGTH,
});
const AnalyticsFleetPageLimitSchema = Type.Integer({
  minimum: 1,
  maximum: ANALYTICS_FLEET_MAX_PAGE_SIZE,
  default: ANALYTICS_FLEET_MAX_PAGE_SIZE,
});

export const AnalyticsFleetQueryV1Schema = Type.Object({
  days: Type.Optional(AnalyticsDaysV1Schema),
  product: Type.Optional(AnalyticsProductFilterV1Schema),
  cursor: Type.Optional(AnalyticsOpaqueCursorSchema),
  limit: Type.Optional(AnalyticsFleetPageLimitSchema),
}, { additionalProperties: false });
export type AnalyticsFleetQueryV1 = Static<typeof AnalyticsFleetQueryV1Schema>;

export const AnalyticsRangeV1Schema = Type.Object({
  from: DateTimeSchema,
  to: DateTimeSchema,
  days: AnalyticsDaysV1Schema,
}, { additionalProperties: false });
export type AnalyticsRangeV1 = Static<typeof AnalyticsRangeV1Schema>;

export const CostCoverageV1Schema = Type.Union([
  Type.Literal("complete"),
  Type.Literal("partial"),
  Type.Literal("unavailable"),
]);
export type CostCoverageV1 = Static<typeof CostCoverageV1Schema>;

export const AnalyticsTotalsV1Schema = Type.Object({
  activeMembers: NonNegativeSafeIntegerSchema,
  productOpens: NonNegativeSafeIntegerSchema,
  productSessions: NonNegativeSafeIntegerSchema,
  runs: NonNegativeSafeIntegerSchema,
  successfulRuns: NonNegativeSafeIntegerSchema,
  failedRuns: NonNegativeSafeIntegerSchema,
  cancelledRuns: NonNegativeSafeIntegerSchema,
  successRate: Type.Union([Type.Number({ minimum: 0, maximum: 1 }), Type.Null()]),
  p50DurationMs: NullableNonNegativeSafeIntegerSchema,
  p95DurationMs: NullableNonNegativeSafeIntegerSchema,
  inputTokens: NonNegativeSafeIntegerSchema,
  outputTokens: NonNegativeSafeIntegerSchema,
  cachedTokens: NonNegativeSafeIntegerSchema,
  estimatedCostMicros: NullableNonNegativeSafeIntegerSchema,
  costCoverage: CostCoverageV1Schema,
}, { additionalProperties: false });
export type AnalyticsTotalsV1 = Static<typeof AnalyticsTotalsV1Schema>;

export const AnalyticsDailyV1Schema = Type.Object({
  date: DateSchema,
  productOpens: NonNegativeSafeIntegerSchema,
  runs: NonNegativeSafeIntegerSchema,
  successfulRuns: NonNegativeSafeIntegerSchema,
  estimatedCostMicros: NullableNonNegativeSafeIntegerSchema,
}, { additionalProperties: false });
export type AnalyticsDailyV1 = Static<typeof AnalyticsDailyV1Schema>;

export const AnalyticsProductSummaryV1Schema = Type.Object({
  product: ProductKeySchema,
  opens: NonNegativeSafeIntegerSchema,
  sessions: NonNegativeSafeIntegerSchema,
  runs: NonNegativeSafeIntegerSchema,
  successRate: Type.Union([Type.Number({ minimum: 0, maximum: 1 }), Type.Null()]),
  estimatedCostMicros: NullableNonNegativeSafeIntegerSchema,
}, { additionalProperties: false });
export type AnalyticsProductSummaryV1 = Static<typeof AnalyticsProductSummaryV1Schema>;

export const AnalyticsSummaryV1Schema = Type.Object({
  version: Type.Literal(1),
  range: AnalyticsRangeV1Schema,
  product: AnalyticsProductFilterV1Schema,
  totals: AnalyticsTotalsV1Schema,
  // An exact 365 x 24-hour UTC interval can touch portions of 366 calendar
  // dates when its endpoints are not midnight. Keep the interval exact while
  // allowing both partial boundary buckets through the aggregate contract.
  daily: Type.Array(AnalyticsDailyV1Schema, { maxItems: 366 }),
  products: Type.Array(AnalyticsProductSummaryV1Schema, { maxItems: 4 }),
}, { additionalProperties: false });
export type AnalyticsSummaryV1 = Static<typeof AnalyticsSummaryV1Schema>;

export const FleetAnalyticsOrganizationV1Schema = Type.Object({
  organizationId: UuidSchema,
  organizationName: Type.String({ minLength: 1, maxLength: 200 }),
  totals: AnalyticsTotalsV1Schema,
}, { additionalProperties: false });
export type FleetAnalyticsOrganizationV1 = Static<typeof FleetAnalyticsOrganizationV1Schema>;

export const FleetAnalyticsV1Schema = Type.Object({
  version: Type.Literal(1),
  range: AnalyticsRangeV1Schema,
  product: AnalyticsProductFilterV1Schema,
  totals: AnalyticsTotalsV1Schema,
  organizations: Type.Array(FleetAnalyticsOrganizationV1Schema, {
    maxItems: ANALYTICS_FLEET_MAX_PAGE_SIZE,
  }),
  nextCursor: Type.Union([AnalyticsOpaqueCursorSchema, Type.Null()]),
}, { additionalProperties: false });
export type FleetAnalyticsV1 = Static<typeof FleetAnalyticsV1Schema>;
